import { useState } from "react";
import { api } from "@/lib/api";
import { CONTACT } from "@/constants/testIds";
import { toast } from "sonner";
import { Mail, MapPin } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received. We will be in touch.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#F4F3EF]">
      <section className="bg-[#0A0A0A] text-white py-24 md:py-32">
        <div className="dta-container">
          <p className="dta-overline text-white/60">Contact</p>
          <h1 className="hero-headline text-5xl md:text-8xl mt-4">Speak plainly.</h1>
          <p className="mt-6 text-white/70 max-w-xl">
            Product questions, wholesale, press, community stories or general enquiries. Message us directly.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="dta-container grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4 space-y-6 text-[#272727]">
            <div>
              <p className="dta-overline text-navy">General</p>
              <a
                href="mailto:contact@downtestadjust.com"
                className="mt-2 inline-flex items-center gap-2 hover:text-[#031D47] transition-colors"
                data-testid="contact-email-general"
              >
                <Mail className="w-4 h-4 text-[#031D47]" strokeWidth={1.5} /> contact@downtestadjust.com
              </a>
            </div>
            <div>
              <p className="dta-overline text-navy">Press</p>
              <a
                href="mailto:contact@downtestadjust.com?subject=Press%20enquiry"
                className="mt-2 inline-flex items-center gap-2 hover:text-[#031D47] transition-colors"
                data-testid="contact-email-press"
              >
                <Mail className="w-4 h-4 text-[#031D47]" strokeWidth={1.5} /> contact@downtestadjust.com
              </a>
            </div>
            <div>
              <p className="dta-overline text-navy">Address</p>
              <p className="mt-2 inline-flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#031D47]" strokeWidth={1.5} /> British designed. Made to order.
              </p>
            </div>
            <p className="text-sm text-[#272727]/70 mt-8 max-w-sm">
              Response times are typically within 2 working days. For urgent mental-health support please see our Support page.
            </p>
          </div>
          <form className="md:col-span-8 space-y-5" onSubmit={submit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <input
                data-testid={CONTACT.name}
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Full name"
                className="bg-transparent border border-black/20 py-4 px-4 focus:border-navy outline-none"
              />
              <input
                data-testid={CONTACT.email}
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email address"
                className="bg-transparent border border-black/20 py-4 px-4 focus:border-navy outline-none"
              />
            </div>
            <input
              data-testid={CONTACT.subject}
              required
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="Subject"
              className="w-full bg-transparent border border-black/20 py-4 px-4 focus:border-navy outline-none"
            />
            <textarea
              data-testid={CONTACT.message}
              required
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Your message"
              rows={7}
              className="w-full bg-transparent border border-black/20 py-4 px-4 focus:border-navy outline-none"
            />
            <button
              disabled={submitting}
              type="submit"
              data-testid={CONTACT.submit}
              className="dta-btn dta-btn-dark disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Send Message"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
